import SupabaseUserService, { useUsers } from '../users/supabaseUserService';
import { useMemo } from 'react';
import { User } from '@shiftly/shared';

/**
 * Helper function to get the full name of a user from their ID
 * @param userId - The ID of the user
 * @returns Promise<string | null> - The full name of the user, or null if not found
 */
export async function getUserFullName(userId: string): Promise<string | null> {
  try {
    const user = await SupabaseUserService.getUser(userId);
    return user?.full_name || null;
  } catch (error) {
    console.error('Error fetching user name:', error);
    return null;
  }
}

/**
 * Helper function to get multiple user names from their IDs
 * @param userIds - Array of user IDs
 * @returns Promise<Record<string, string>> - Object mapping user IDs to their names
 */
export async function getUserNames(userIds: string[]): Promise<Record<string, string>> {
  try {
    const users = await SupabaseUserService.getUsers();
    const userMap: Record<string, string> = {};
    
    userIds.forEach(id => {
      const user = users.find(u => u.id === id);
      if (user) {
        userMap[id] = user.full_name;
      }
    });
    
    return userMap;
  } catch (error) {
    console.error('Error fetching user names:', error);
    return {};
  }
}

/**
 * Helper function to get a user's full name synchronously from a cached users array
 * @param userId - The ID of the user
 * @param users - Array of users to search in
 * @returns string | null - The full name of the user, or null if not found
 */
export function getUserFullNameFromCache(userId: string, users: User[]): string | null {
  const user = users.find(u => u.id === userId);
  return user?.full_name || null;
}

/**
 * React hook to get a user's full name from their ID
 * @param userId - The ID of the user
 * @returns string | null - The full name of the user, or null if not found
 */
export function useUserFullName(userId: string): string | null {
  const { data: users = [] } = useUsers();
  
  return useMemo(() => {
    if (!userId || !users.length) return null;
    return getUserFullNameFromCache(userId, users);
  }, [userId, users]);
}

/**
 * React hook to get multiple user names from their IDs
 * @param userIds - Array of user IDs
 * @returns Record<string, string> - Object mapping user IDs to their names
 */
export function useUserNames(userIds: string[]): Record<string, string> {
  const { data: users = [] } = useUsers();
  
  return useMemo(() => {
    if (!userIds.length || !users.length) return {};
    
    const userMap: Record<string, string> = {};
    userIds.forEach(id => {
      const user = users.find(u => u.id === id);
      if (user) {
        userMap[id] = user.full_name;
      }
    });
    
    return userMap;
  }, [userIds, users]);
}
